import { useState, useEffect } from 'react';
import { employeesAPI } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { BarChart3, Clock, Filter, X } from 'lucide-react';

function formatHoursSigned(h) {
  const sign = h >= 0 ? '+' : '';
  return `${sign}${h}h`;
}

function StatCard({ label, totalHours, expected, diff, shiftCount, numWeeks, typeCounts }) {
  return (
    <div className="bg-slate-50 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-700">{label}</span>
        <span className="text-xs text-slate-400 font-data">{numWeeks} sem. | {shiftCount} shifts</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center">
          <div className="text-lg font-bold font-data text-slate-900">{totalHours}h</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Effectuées</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold font-data text-slate-500">{expected}h</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Attendues</div>
        </div>
        <div className="text-center">
          <div className={`text-lg font-bold font-data ${diff >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
            {formatHoursSigned(diff)}
          </div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Différence</div>
        </div>
      </div>
      {typeCounts && Object.keys(typeCounts).length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {typeCounts.normal > 0 && <span className="text-[10px] font-medium bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{typeCounts.normal} normal</span>}
          {typeCounts.ecole > 0 && <span className="text-[10px] font-medium bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full">{typeCounts.ecole} école</span>}
          {typeCounts.conge > 0 && <span className="text-[10px] font-medium bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">{typeCounts.conge} congé</span>}
          {typeCounts.maladie > 0 && <span className="text-[10px] font-medium bg-red-50 text-red-700 px-2 py-0.5 rounded-full">{typeCounts.maladie} maladie</span>}
        </div>
      )}
    </div>
  );
}

export default function EmployeeRecapDialog({ open, onClose, employee, isAdmin }) {
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  useEffect(() => {
    if (open && employee) {
      setShowCustom(false);
      setCustomStart('');
      setCustomEnd('');
      fetchRecap();
    }
  }, [open, employee]);

  const fetchRecap = async (startDate, endDate) => {
    if (!employee) return;
    setLoading(true);
    try {
      const params = {};
      if (startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      }
      const res = await employeesAPI.recap(employee.id, params);
      setRecap(res.data);
    } catch { /* */ }
    setLoading(false);
  };

  const handleCustomFilter = () => {
    if (!customStart || !customEnd) return;
    fetchRecap(customStart, customEnd);
  };

  const clearCustom = () => {
    setShowCustom(false);
    setCustomStart('');
    setCustomEnd('');
    fetchRecap();
  };

  if (!employee) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="employee-recap-dialog" className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" strokeWidth={1.5} />
            Récapitulatif
          </DialogTitle>
          <DialogDescription>
            {employee.first_name} {employee.last_name.toUpperCase()} — Contrat {employee.contract_hours}h/sem
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-slate-400 text-sm">Chargement...</div>
        ) : recap ? (
          <div className="space-y-4 py-2">
            {/* Overtime balance - admin only */}
            {isAdmin && (
              <div className="flex items-center justify-between bg-slate-900 text-white rounded-xl p-4">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 opacity-70" strokeWidth={1.5} />
                  <span className="text-sm font-medium">Solde heures supplémentaires</span>
                </div>
                <span className="text-xl font-bold font-data">
                  {recap.overtime_balance >= 0 ? '+' : ''}{recap.overtime_balance}h
                </span>
              </div>
            )}

            {/* Custom date filter */}
            {!showCustom ? (
              <Button data-testid="show-custom-filter-btn" variant="outline" size="sm" className="w-full gap-2"
                onClick={() => setShowCustom(true)}>
                <Filter className="w-3.5 h-3.5" strokeWidth={1.5} />Filtrer par période personnalisée
              </Button>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2 animate-fade-in-up">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold text-blue-800">Période personnalisée</Label>
                  <button data-testid="clear-custom-filter-btn" onClick={clearCustom} className="text-blue-400 hover:text-blue-600">
                    <X className="w-3.5 h-3.5" strokeWidth={2} />
                  </button>
                </div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1 space-y-1">
                    <Label className="text-[10px] text-blue-700">Du</Label>
                    <Input data-testid="custom-start-date" type="date" value={customStart}
                      onChange={e => setCustomStart(e.target.value)} className="h-8 text-xs font-data" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label className="text-[10px] text-blue-700">Au</Label>
                    <Input data-testid="custom-end-date" type="date" value={customEnd}
                      onChange={e => setCustomEnd(e.target.value)} className="h-8 text-xs font-data" />
                  </div>
                  <Button data-testid="apply-custom-filter-btn" size="sm" className="h-8" onClick={handleCustomFilter}
                    disabled={!customStart || !customEnd}>
                    Filtrer
                  </Button>
                </div>
              </div>
            )}

            {/* Custom period result */}
            {recap.custom && (
              <StatCard
                label={recap.custom_label}
                totalHours={recap.custom.total_hours}
                expected={recap.custom.expected_hours}
                diff={recap.custom.diff}
                shiftCount={recap.custom.shift_count}
                numWeeks={recap.custom.num_weeks}
                typeCounts={recap.custom.type_counts}
              />
            )}

            {!recap.custom && <Separator />}

            <StatCard
              label={`Ce mois (${recap.month_label})`}
              totalHours={recap.month.total_hours}
              expected={recap.month.expected_hours}
              diff={recap.month.diff}
              shiftCount={recap.month.shift_count}
              numWeeks={recap.month.num_weeks}
              typeCounts={recap.month.type_counts}
            />
            <StatCard
              label={`Cette année (${recap.year_label})`}
              totalHours={recap.year.total_hours}
              expected={recap.year.expected_hours}
              diff={recap.year.diff}
              shiftCount={recap.year.shift_count}
              numWeeks={recap.year.num_weeks}
              typeCounts={recap.year.type_counts}
            />
            <StatCard
              label="Depuis l'embauche"
              totalHours={recap.all_time.total_hours}
              expected={recap.all_time.expected_hours}
              diff={recap.all_time.diff}
              shiftCount={recap.all_time.shift_count}
              numWeeks={recap.all_time.num_weeks}
              typeCounts={recap.all_time.type_counts}
            />
          </div>
        ) : (
          <div className="py-8 text-center text-slate-400 text-sm">Aucune donnée</div>
        )}
      </DialogContent>
    </Dialog>
  );
}
